<template>
    <section class="tables new-updated-design px-0 px-md-0 pt-4 pt-md-0">
        <div class="row">
            <div class="col-lg-12 grid-margin stretch-card properties-table">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title">{{ $t('booking_requests.page_title') }}</h4>
                    </div>
                    <div class="card-body table-loading-wrap">
                        <div v-if="isAccepting" class="loading-overlay">
                            <div class="loading-overlay__content">
                                <div class="spinner"></div>
                                <p>{{ $t('payment.processing') }}</p>
                            </div>
                        </div>
                        <vue-good-table mode="remote" :rows="rows" :columns="columns" :totalRows="totalRecords"
                            :pagination-options="paginationOptions" :sort-options="{ enabled: false }"
                            @on-page-change="onPageChange" @on-per-page-change="onPerPageChange">
                            <template slot="table-row" slot-scope="props">
                                <span v-if="props.column.field === 'guest_name'">{{ props.row.guest?.name || '-' }}</span>
                                <span v-else-if="props.column.field === 'guest_email'">{{ props.row.guest?.email || '-' }}</span>
                                <span v-else-if="props.column.field === 'guest_contact'">
                                    <a v-if="getGuestPhone(props.row)" class="guest-contact-link"
                                        :href="'tel:' + getGuestPhone(props.row)">{{ getGuestPhone(props.row) }}</a>
                                    <span v-else>-</span>
                                </span>
                                <span v-else-if="props.column.field === 'property_type'">
                                    {{ props.row.property?.property_type ? $t('property_detail.' + props.row.property.property_type) : '-' }}
                                </span>
                                <span v-else-if="props.column.field === 'start_date'">{{ formatDate(props.row.request_payload?.startDate) }}</span>
                                <span v-else-if="props.column.field === 'end_date'">{{ formatDate(props.row.request_payload?.endDate) }}</span>
                                <span v-else-if="props.column.field === 'guests'">{{ props.row.request_payload?.number_of_guests || '-' }}</span>
                                <span v-else-if="props.column.field === 'price'">
                                    {{ props.row.request_payload?.paymentCurrency || props.row.property?.currency || '' }}
                                    {{ props.row.request_payload?.totalPrice ?? '-' }}
                                </span>
                                <span v-else-if="props.column.field === 'status'">
                                    <span>{{ formatStatus(getEffectiveStatus(props.row)) }}</span>
                                    <div v-if="hasDateConflict(props.row)" class="date-conflict-alert">
                                        <div class="date-conflict-alert__title">{{ $t('booking_requests.date_conflict_title') }}</div>
                                        <p>{{ getConflictDetail(props.row) }}</p>
                                        <p v-if="getGuestPhone(props.row)" class="date-conflict-alert__contact">
                                            {{ $t('booking_requests.guest_contact') }}:
                                            <a :href="'tel:' + getGuestPhone(props.row)">{{ getGuestPhone(props.row) }}</a>
                                        </p>
                                        <p class="date-conflict-alert__hint">{{ $t('booking_requests.date_conflict_contact') }}</p>
                                    </div>
                                </span>
                                <span v-else-if="props.column.field === 'responded_at'">{{ formatDateTime(props.row.responded_at) }}</span>
                                <span v-else-if="props.column.field === 'action'" class="action-cell">
                                    <div class="action-buttons">
                                        <b-button size="sm" variant="success" class="mr-1"
                                            :title="getAcceptDisabledMessage(props.row)"
                                            :disabled="isActionDisabled(props.row)"
                                            @click="acceptRequest(props.row)">
                                            {{ $t('booking_requests.accept') }}
                                        </b-button>
                                        <b-button size="sm" variant="danger"
                                            :disabled="isRejectDisabled(props.row)"
                                            @click="rejectRequest(props.row)">
                                            {{ $t('booking_requests.reject') }}
                                        </b-button>
                                    </div>
                                </span>
                            </template>
                            <template slot="emptystate">
                                <div class="text-center text-muted py-2 w-100">
                                    {{ $t('common.no_data_available') }}
                                </div>
                            </template>
                        </vue-good-table>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import {
    STRIPE_STATE_NONE,
    STRIPE_STATE_PENDING,
    STRIPE_STATE_REJECTED,
    STRIPE_STATE_VERIFIED,
    resolveStripeConnectState,
    canAcceptBookingPayments,
    parseStripeErrorResponse,
} from '@/utils/stripeConnectStatus';

export default {
    name: 'BookingRequests',
    data() {
        return {
            rows: [],
            totalRecords: 0,
            isAccepting: false,
            stripeConnectAccount: null,
            stripeConnectMeta: {},
            serverParams: {
                role: 'host',
                page: 1,
                perPage: 10,
            },
            paginationOptions: {
                enabled: true,
                dropdownAllowAll: false,
                perPageDropdown: [10, 20, 50, 100, 200],
                nextLabel: this.$t('pagination.next'),
                prevLabel: this.$t('pagination.Previous'),
                rowsPerPageLabel: this.$t('pagination.rowsPerPage'),
                ofLabel: this.$t('pagination.of'),
            },
            columns: [
                { label: this.$t('booking_requests.guest'), field: 'guest_name' },
                { label: this.$t('booking_requests.guest_email'), field: 'guest_email' },
                { label: this.$t('booking_requests.guest_contact'), field: 'guest_contact' },
                { label: this.$t('booking_requests.property_type'), field: 'property_type' },
                { label: this.$t('booking_requests.start_date'), field: 'start_date' },
                { label: this.$t('booking_requests.end_date'), field: 'end_date' },
                { label: this.$t('booking_requests.guests'), field: 'guests' },
                { label: this.$t('booking_requests.total'), field: 'price' },
                { label: this.$t('booking_requests.status'), field: 'status' },
                { label: this.$t('booking_requests.responded_at'), field: 'responded_at' },
                { label: this.$t('booking_requests.action'), field: 'action' },
            ],
        };
    },
    methods: {
        loadStripeConnectStatus() {
            return new Promise((resolve) => {
                API.getConnectAccount((data) => {
                    this.stripeConnectAccount = data.data || null;
                    this.stripeConnectMeta = {
                        stripe_state: data.stripe_state,
                        can_accept_payments: data.can_accept_payments,
                    };
                    const verified = canAcceptBookingPayments(this.stripeConnectAccount, this.stripeConnectMeta);
                    localStorage.setItem('stripeVerified', verified ? 'true' : 'false');
                    resolve();
                }, () => {
                    this.stripeConnectAccount = null;
                    this.stripeConnectMeta = {};
                    localStorage.setItem('stripeVerified', 'false');
                    resolve();
                });
            });
        },
        loadBookingRequests() {
            API.getBookingRequests(this.serverParams, (data) => {
                this.rows = (data.data || []).map((row) => ({
                    ...row,
                    actionLoading: false,
                }));
                this.totalRecords = data.totalRecords || 0;
            }, () => {
                this.rows = [];
                this.totalRecords = 0;
            });
        },
        onPageChange(params) {
            this.serverParams.page = params.currentPage;
            this.loadBookingRequests();
        },
        onPerPageChange(params) {
            this.serverParams.perPage = params.currentPerPage;
            this.serverParams.page = 1;
            this.loadBookingRequests();
        },
        formatDate(date) {
            if (!date) return '-';
            return moment(date).format('DD-MM-YYYY');
        },
        formatDateTime(date) {
            if (!date) return '-';
            return moment(date).format('DD-MM-YYYY HH:mm');
        },
        formatStatus(status) {
            if (!status) return '-';
            return status.charAt(0).toUpperCase() + status.slice(1);
        },
        getEffectiveStatus(row) {
            if (!row) return '';
            if (row.status !== 'pending') return row.status;
            if (row.expires_at && moment().isAfter(moment(row.expires_at))) return 'expired';
            return 'pending';
        },
        isActionDisabled(row) {
            return row.actionLoading || this.getEffectiveStatus(row) !== 'pending' || this.hasDateConflict(row);
        },
        isRejectDisabled(row) {
            return row.actionLoading || this.getEffectiveStatus(row) !== 'pending';
        },
        getGuestPhone(row) {
            return row?.request_payload?.phone || row?.guest?.mobile_phone || '';
        },
        hasDateConflict(row) {
            return this.getEffectiveStatus(row) === 'pending' && !!row?.has_date_conflict;
        },
        getConflictDateRange(row) {
            if (row?.conflict_start_date && row?.conflict_end_date) {
                return `${this.formatDate(row.conflict_start_date)} - ${this.formatDate(row.conflict_end_date)}`;
            }
            return '';
        },
        getConflictDetail(row) {
            const dates = this.getConflictDateRange(row);
            if (dates) {
                return this.$t('booking_requests.date_conflict_message', { dates });
            }
            return this.$t('booking_requests.date_conflict_message_short');
        },
        getConflictMessage(row) {
            const parts = [this.getConflictDetail(row), this.$t('booking_requests.date_conflict_contact')];
            const phone = this.getGuestPhone(row);
            if (phone) {
                parts.push(`${this.$t('booking_requests.guest_contact')}: ${phone}`);
            }
            return parts.join(' ');
        },
        getAcceptDisabledMessage(row) {
            if (this.hasDateConflict(row)) {
                return this.getConflictMessage(row);
            }
            return '';
        },
        getStripeState() {
            return resolveStripeConnectState(this.stripeConnectAccount, this.stripeConnectMeta);
        },
        async ensureStripeReadyForAccept() {
            await this.loadStripeConnectStatus();
            const state = this.getStripeState();

            if (state === STRIPE_STATE_VERIFIED) {
                return true;
            }

            if (state === STRIPE_STATE_NONE) {
                const result = await this.$swal({
                    title: this.$t('stripe_connect.setup_required_title'),
                    text: this.$t('stripe_connect.setup_required_text'),
                    icon: 'info',
                    type: 'info',
                    showCancelButton: true,
                    confirmButtonText: this.$t('stripe_connect.setup_now'),
                    cancelButtonText: this.$t('sweetalert.cancel'),
                });
                if (result.value) {
                    this.$router.push({ path: '/add_property', query: { from: 'booking_requests', setup: 'stripe' } });
                }
                return false;
            }

            if (state === STRIPE_STATE_REJECTED) {
                await this.$swal({
                    icon: 'warning',
                    type: 'warning',
                    title: this.$t('stripe_connect.verification_rejected_title'),
                    text: this.$t('stripe_connect.verification_rejected_text'),
                });
                return false;
            }

            await this.$swal({
                icon: 'info',
                type: 'info',
                title: this.$t('stripe_connect.verification_pending_title'),
                text: this.$t('stripe_connect.verification_pending_text'),
            });
            return false;
        },
        handleAcceptStripeError(error) {
            const conflictCode = error?.code || error?.response?.data?.code;
            const conflictMessage = error?.message || error?.response?.data?.message;
            if (conflictCode === 'booking_dates_conflict') {
                this.$swal({
                    icon: 'warning',
                    type: 'warning',
                    title: this.$t('alerts.error'),
                    text: conflictMessage || 'This request overlaps with an accepted booking.',
                });
                return;
            }

            const parsed = parseStripeErrorResponse(error);
            if (parsed.code === 'booking_dates_conflict') {
                this.$swal({
                    icon: 'warning',
                    type: 'warning',
                    title: this.$t('alerts.error'),
                    text: parsed.message || 'This request overlaps with an accepted booking.',
                });
                return;
            }
            if (parsed.code === 'stripe_connect_required') {
                this.$swal({
                    icon: 'info',
                    type: 'info',
                    title: this.$t('stripe_connect.setup_required_title'),
                    text: parsed.message || this.$t('stripe_connect.setup_required_text'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('stripe_connect.setup_now'),
                }).then((result) => {
                    if (result.value) {
                        this.$router.push({ path: '/add_property', query: { from: 'booking_requests', setup: 'stripe' } });
                    }
                });
                return;
            }
            if (parsed.code === 'stripe_connect_pending' || parsed.code === 'stripe_connect_rejected') {
                const isRejected = parsed.code === 'stripe_connect_rejected';
                this.$swal({
                    icon: isRejected ? 'warning' : 'info',
                    type: isRejected ? 'warning' : 'info',
                    title: isRejected
                        ? this.$t('stripe_connect.verification_rejected_title')
                        : this.$t('stripe_connect.verification_pending_title'),
                    text: parsed.message || (isRejected
                        ? this.$t('stripe_connect.verification_rejected_text')
                        : this.$t('stripe_connect.verification_pending_text')),
                });
                return;
            }
            this.$swal({
                icon: 'error',
                type: 'error',
                title: this.$t('alerts.error'),
                text: parsed.message || this.$t('alerts.error'),
            });
        },
        async acceptRequest(row) {
            if (this.getEffectiveStatus(row) !== 'pending') return;
            if (this.hasDateConflict(row)) {
                await this.$swal({
                    icon: 'warning',
                    type: 'warning',
                    title: this.$t('alerts.error'),
                    text: this.getConflictMessage(row),
                });
                return;
            }

            const stripeReady = await this.ensureStripeReadyForAccept();
            if (!stripeReady) return;

            const confirmation = await this.$swal({
                title: this.$t('booking_requests.confirm_accept_title'),
                text: this.$t('booking_requests.confirm_accept_text'),
                icon: 'warning',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('booking_requests.confirm_accept_yes'),
                cancelButtonText: this.$t('sweetalert.cancel'),
            });

            if (!confirmation.value) return;

            this.isAccepting = true;
            this.$set(row, 'actionLoading', true);
            API.acceptBookingRequest(row.id, (data) => {
                this.isAccepting = false;
                this.$set(row, 'actionLoading', false);
                if (data.status === 200 || data.success) {
                    this.$swal({
                        icon: 'success',
                        type: 'success',
                        title: this.$t('alerts.success'),
                        text: data.message || 'Booking request accepted successfully.',
                    });
                    this.loadBookingRequests();
                } else {
                    this.handleAcceptStripeError(data);
                    this.loadBookingRequests();
                }
            }, (error) => {
                this.isAccepting = false;
                this.$set(row, 'actionLoading', false);
                this.handleAcceptStripeError(error);
                this.loadBookingRequests();
            });
        },
        async rejectRequest(row) {
            if (this.getEffectiveStatus(row) !== 'pending') return;

            const confirmation = await this.$swal({
                title: this.$t('booking_requests.confirm_reject_title'),
                text: this.$t('booking_requests.confirm_reject_text'),
                icon: 'warning',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('booking_requests.confirm_reject_yes'),
                cancelButtonText: this.$t('sweetalert.cancel'),
            });

            if (!confirmation.value) return;

            const reasonResult = await this.$swal({
                title: this.$t('booking_requests.reject_reason_title'),
                input: 'text',
                inputPlaceholder: this.$t('booking_requests.reject_reason_placeholder'),
                showCancelButton: true,
                confirmButtonText: this.$t('booking_requests.confirm_reject_submit'),
            });

            if (reasonResult.dismiss) return;

            this.$set(row, 'actionLoading', true);
            API.rejectBookingRequest(row.id, { reason: reasonResult.value || '' }, (data) => {
                this.$set(row, 'actionLoading', false);
                if (data.status === 200) {
                    this.$swal({
                        icon: 'success',
                        type: 'success',
                        title: this.$t('alerts.success'),
                        text: data.message || 'Booking request rejected successfully.',
                    });
                    this.loadBookingRequests();
                } else {
                    this.$swal({
                        icon: 'error',
                        type: 'error',
                        title: this.$t('alerts.error'),
                        text: data.message || this.$t('alerts.error'),
                    });
                    this.loadBookingRequests();
                }
            }, (error) => {
                this.$set(row, 'actionLoading', false);
                this.$swal({
                    icon: 'error',
                    type: 'error',
                    title: this.$t('alerts.error'),
                    text: error?.message || error?.error || this.$t('alerts.error'),
                });
                this.loadBookingRequests();
            });
        },
    },
    async mounted() {
        await this.loadStripeConnectStatus();
        this.loadBookingRequests();

        if (this.$route.query.stripe_pending === '1') {
            await this.$swal({
                icon: 'info',
                type: 'info',
                title: this.$t('stripe_connect.verification_pending_title'),
                text: this.$t('stripe_connect.verification_pending_text'),
            });
            this.$router.replace({ path: '/booking_requests' });
        }
    },
};
</script>

<style scoped>
.table-loading-wrap {
    position: relative;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
}

.loading-overlay__content {
    text-align: center;
}

.loading-overlay__content p {
    margin-top: 12px;
    margin-bottom: 0;
    font-weight: 500;
    color: #333;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 5px solid #ccc;
    border-top-color: #70D3FF;
    border-radius: 50%;
    animation: booking-request-spin 1s linear infinite;
    margin: 0 auto;
}

.conflict-warning {
    white-space: normal;
    max-width: 260px;
    color: #b45309;
    font-size: 12px;
    font-weight: 500;
}

.action-cell {
    white-space: normal;
    min-width: 150px;
}

.action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.date-conflict-alert {
    margin-top: 8px;
    padding: 10px 12px;
    background: #fff8e6;
    border: 1px solid #f0c36d;
    border-left: 4px solid #e6a700;
    border-radius: 6px;
    text-align: left;
    white-space: normal;
    max-width: 300px;
}

.date-conflict-alert__title {
    font-weight: 600;
    color: #8a5a00;
    margin-bottom: 4px;
    font-size: 13px;
}

.date-conflict-alert p {
    margin: 0 0 6px;
    font-size: 12px;
    color: #5c4a2a;
    line-height: 1.45;
}

.date-conflict-alert p:last-child {
    margin-bottom: 0;
}

.date-conflict-alert__contact a {
    color: #0d6efd;
    font-weight: 500;
    text-decoration: none;
}

.date-conflict-alert__contact a:hover {
    text-decoration: underline;
}

.date-conflict-alert__hint {
    font-style: italic;
}

.guest-contact-link {
    color: #0d6efd;
    font-weight: 500;
    text-decoration: none;
    white-space: nowrap;
}

.guest-contact-link:hover {
    text-decoration: underline;
}

@keyframes booking-request-spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
